import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  Renderer2,
} from '@angular/core';

@Directive({
  selector: '[appMaskInput]',
  exportAs: 'appMaskInput',
})
export class MaskInputDirective {
  @Input() maskType: 'aadhaar' | 'pan' = 'aadhaar';

  private _masked = true;
  private actualValue = '';
  @Output() maskedChange = new EventEmitter<boolean>();
  
  @Input()
  set masked(value: boolean) {
    if (!this._masked) {
      this.syncActualValueFromElement();
    }

    this._masked = value;
    this.updateView();
  }

  get masked(): boolean {
    return this._masked;
  }

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
  ) {}

  // =========================
  // INPUT EVENT
  // =========================

  @HostListener('input', ['$event'])
  onInput(event: any): void {
    let value = event.target.value || '';

    value = this.cleanValue(value);

    // Max Length
    value = this.applyMaxLength(value);

    this.actualValue = value;

    this.updateView();
  }

  // =========================
  // FOCUS
  // =========================

  @HostListener('focus')
  onFocus(): void {
    if (!this.masked) {
      this.syncActualValueFromElement();

      this.renderer.setProperty(
        this.el.nativeElement,
        'value',
        this.formatValue(this.actualValue),
      );
    }
  }

  // =========================
  // BLUR
  // =========================

  @HostListener('blur')
  onBlur(): void {
    this.updateView();
  }

  // =========================
  // PUBLIC TOGGLE METHOD
  // =========================

  toggleMask(): void {
    if (!this.masked) {
      this.syncActualValueFromElement();
    }

    this.setMaskedState(!this.masked);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.masked && this.isEditKey(event)) {
      this.setMaskedState(false);
    }
  }

  @HostListener('paste')
  @HostListener('cut')
  onClipboardEdit(): void {
    if (this.masked) {
      this.setMaskedState(false);
    }
  }

  // =========================
  // UPDATE VIEW
  // =========================

  private updateView(): void {
    let displayValue = '';

    if (this.masked) {
      displayValue = this.getMaskedValue(this.actualValue);
    } else {
      displayValue = this.formatValue(this.actualValue);
    }

    this.renderer.setProperty(this.el.nativeElement, 'value', displayValue);
  }

  // =========================
  // CLEAN VALUE
  // =========================

  private cleanValue(value: string): string {
    switch (this.maskType) {
      case 'aadhaar':
        return value.replace(/\D/g, '');

      case 'pan':
        return value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

      default:
        return value;
    }
  }

  private syncActualValueFromElement(): void {
    const value = this.cleanValue(this.el.nativeElement.value || '');

    if (value) {
      this.actualValue = this.applyMaxLength(value);
    }
  }

  private setMaskedState(masked: boolean): void {
    this._masked = masked;
    this.maskedChange.emit(this._masked);
    this.updateView();
  }

  private isEditKey(event: KeyboardEvent): boolean {
    return (
      (event.key.length === 1 ||
        event.key === 'Backspace' ||
        event.key === 'Delete') &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey
    );
  }

  // =========================
  // MAX LENGTH
  // =========================

  private applyMaxLength(value: string): string {
    switch (this.maskType) {
      case 'aadhaar':
        return value.substring(0, 12);

      case 'pan':
        return value.substring(0, 10);

      default:
        return value;
    }
  }

  // =========================
  // FORMAT VALUE
  // =========================

  private formatValue(value: string): string {
    switch (this.maskType) {
      case 'aadhaar':
        return value;

      default:
        return value;
    }
  }

  // =========================
  // MASK VALUE
  // =========================

  private getMaskedValue(value: string): string {
    switch (this.maskType) {
      case 'aadhaar':
        return this.maskAadhaar(value);

      case 'pan':
        return this.maskPan(value);

      default:
        return value;
    }
  }

  // =========================
  // AADHAAR
  // =========================

  private maskAadhaar(value: string): string {
    if (value.length <= 4) {
      return value;
    }

    return `XXXX XXXX ${value.slice(-4)}`;
  }

  // =========================
  // PAN
  // =========================

  private maskPan(value: string): string {
    if (value.length < 10) {
      return value;
    }

    return value.substring(0, 2) + 'XXXXX' + value.substring(7);
  }
}
